import { Node } from 'reactflow';
import {
  OperationalModel,
  OperationalStatus,
  SubLayersWithoutValidStatus,
  SubLayersWithScopeFilterOnly,
  SubLayerTypes,
} from '@constants/canvas/layers';
import {
  CiaFilters,
  ProjectFiltersContextProps,
} from '@context/ProjectFilters/types';
import { NodeDTO } from '@store/services/nodes/types';
import { isCriteriaFieldsMatch } from '@utils/canvasHelpers';
import { checkScopeFilter } from '@utils/domains';

/**
 * Every category the shared predicate evaluates: the filter-context
 * categories plus `domains`, which is Scope and is not a field of
 * `ProjectFiltersContextProps`.
 */
export type ModelFilters = Pick<
  ProjectFiltersContextProps,
  | 'validationStatus'
  | 'reviewStatus'
  | 'operationalStatuses'
  | 'operatingModelStatuses'
  | 'partners'
  | 'ciaFilters'
> & {
  domains: string[];
};

/**
 * The slice of a node DTO the predicate reads. Exported so that anything
 * deriving per-value facts from the same fields — the popover's counts — is
 * typed against the exact paths the filter itself uses.
 */
export type FilterableNodeDto = Partial<
  Pick<
    NodeDTO,
    | 'type'
    | 'valid_status'
    | 'review_required'
    | 'partners'
    | 'cia'
    | 'is_reported'
    | 'domainNodes'
  >
> & {
  meta_data?: {
    operational_status?: OperationalStatus;
    operational_model?: OperationalModel;
  };
};

const hasCiaSelection = (ciaFilters: CiaFilters) =>
  Object.values(ciaFilters).some((fields) =>
    Object.values(fields).some((answers) => answers?.length),
  );

/**
 * Whether any category other than Scope carries a selection.
 *
 * Scope is left out on purpose: the sub-layers in
 * `SubLayersWithScopeFilterOnly` answer to Scope alone and are hidden
 * wholesale once any other filter is active, so this is the test that rule
 * needs.
 */
export const hasActiveModelFilter = ({
  validationStatus,
  reviewStatus,
  operationalStatuses,
  operatingModelStatuses,
  partners,
  ciaFilters,
}: ModelFilters) =>
  Boolean(
    validationStatus.length ||
      reviewStatus.length ||
      operationalStatuses.length ||
      operatingModelStatuses.length ||
      partners.length ||
      hasCiaSelection(ciaFilters),
  );

/**
 * The one client-side filter predicate: AND across categories, OR within a
 * category, a node with no value for an active category is hidden.
 *
 * Scope is evaluated first and for every sub-layer, so the scope-only
 * sub-layers are scoped like everything else before the "any other filter
 * hides them" rule runs. It goes through `checkScopeFilter`, which matches
 * only `direct` domain rows (AC7.c) and treats the Unassigned and
 * No-operationalised ids as Scope values in their own right.
 */
export const matchesModelFiltersDto = (
  dto: FilterableNodeDto | undefined,
  subLayer: SubLayerTypes | undefined,
  filters: ModelFilters,
) => {
  const {
    domains,
    validationStatus,
    reviewStatus,
    operationalStatuses,
    operatingModelStatuses,
    partners,
    ciaFilters,
  } = filters;

  if (
    domains.length &&
    !checkScopeFilter(domains, dto?.type, dto?.domainNodes)
  ) {
    return false;
  }

  if (subLayer && SubLayersWithScopeFilterOnly.includes(subLayer)) {
    return !hasActiveModelFilter(filters);
  }

  if (reviewStatus.length && !dto?.review_required) return false;

  if (validationStatus.length) {
    const hasNoValidStatus =
      !!subLayer && SubLayersWithoutValidStatus.includes(subLayer);

    const validStatus = dto?.valid_status;

    if (
      hasNoValidStatus ||
      !validStatus ||
      !validationStatus.includes(validStatus)
    ) {
      return false;
    }
  }

  const operationalStatus = dto?.meta_data?.operational_status;

  if (
    operationalStatuses.length &&
    (!operationalStatus || !operationalStatuses.includes(operationalStatus))
  ) {
    return false;
  }

  const operatingModel = dto?.meta_data?.operational_model;

  if (
    operatingModelStatuses.length &&
    (!operatingModel || !operatingModelStatuses.includes(operatingModel))
  ) {
    return false;
  }

  if (
    partners.length &&
    !partners.some((partner) => dto?.partners?.includes(partner))
  ) {
    return false;
  }

  if (hasCiaSelection(ciaFilters)) {
    const isAllMatch =
      isCriteriaFieldsMatch(
        ciaFilters.assurance_level,
        dto?.cia?.assurance_level?.criterias,
      ) &&
      isCriteriaFieldsMatch(
        ciaFilters.coverage,
        dto?.cia?.coverage?.criterias,
      ) &&
      isCriteriaFieldsMatch(
        ciaFilters.applicability,
        dto?.cia?.applicability?.criterias,
      ) &&
      dto?.is_reported !== false;

    if (!isAllMatch) return false;
  }

  return true;
};

export const matchesModelFilters = (node: Node, filters: ModelFilters) =>
  matchesModelFiltersDto(
    node.data?.dto,
    node.parentNode as SubLayerTypes,
    filters,
  );
